/**
 * Atribuir uma disciplina inteira, num gesto.
 *
 * **A tarefa é uma frase**, e precisava ser uma tela: *"Microeconomia roda em
 * dez turmas; Ana leva duas, Bruno três, Carla cinco"*. O cadastro genérico
 * cobre isso em três telas — criar o grupo, ligar as ofertas, ligar as cotas —
 * e nenhuma delas mostra quantas turmas existem nem se as cotas fecham a
 * conta.
 *
 * O recorte é a **disciplina** e não o grupo, porque é assim que a pessoa
 * pensa: ela abre a disciplina e vê as turmas dela. O grupo é o registro que
 * sobra disso, e ela não deveria precisar saber que ele existe.
 *
 * **A conta fica à vista o tempo todo.** Quatro turmas e cotas que somam três
 * é uma grade que não fecha, e descobrir isso cinco minutos depois, no
 * resultado do processamento, é tarde. A linha de soma diz, enquanto se
 * digita, quanto falta ou quanto sobra.
 *
 * **O que esta tela NÃO decide.** Quais turmas ficam com quem — isso é do
 * motor, e é o ponto inteiro. Aqui se declara quem pode e quantas; a escolha
 * acontece junto com a grade, porque duas turmas em dias opostos são duas
 * idas à escola e só quem monta o horário sabe disso.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ErroApi,
  atribuicao,
  type Cota,
  type DisciplinaAtribuivel,
  type EstadoDaAtribuicao,
} from '../api'
import { Icon } from '../components/Icon'
import { rotulosDistintos } from '../rotulos'

/** `2` → `2 turmas`, porque a frase é lida por gente. */
function turmas(n: number): string {
  return n === 1 ? '1 turma' : `${n} turmas`
}

export function Atribuir({ projeto }: { projeto: string }) {
  const [disciplinas, setDisciplinas] = useState<DisciplinaAtribuivel[]>([])
  const [escolhida, setEscolhida] = useState<string | null>(null)
  const [estado, setEstado] = useState<EstadoDaAtribuicao | null>(null)

  const [noGrupo, setNoGrupo] = useState<Set<string>>(new Set())
  const [cotas, setCotas] = useState<Cota[]>([])
  /** Professores por turma: 1, ou 2 para a dupla de co-docência (P10). */
  const [porTurma, setPorTurma] = useState(1)

  const [busca, setBusca] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)
  const [ocupado, setOcupado] = useState(false)

  const carregarLista = useCallback(() => {
    atribuicao
      .disciplinas(projeto)
      .then(setDisciplinas)
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar'))
  }, [projeto])

  useEffect(carregarLista, [carregarLista])

  useEffect(() => {
    if (!escolhida) {
      setEstado(null)
      return
    }
    setSalvo(false)
    atribuicao
      .ler(projeto, escolhida)
      .then((e) => {
        setEstado(e)
        // Sem grupo ainda: a proposta é todas as turmas, que é o caso comum.
        const jaNoGrupo = e.ofertas.filter((o) => o.atribuicao_id).map((o) => o.id)
        setNoGrupo(new Set(jaNoGrupo.length > 0 ? jaNoGrupo : e.ofertas.map((o) => o.id)))
        setCotas(e.cotas)
        setPorTurma(e.professores_por_oferta ?? 1)
      })
      .catch((x) => setErro(x instanceof ErroApi ? x.message : 'não foi possível carregar'))
  }, [projeto, escolhida])

  const somaMinima = cotas.reduce((s, c) => s + (c.minimo || 0), 0)
  const semTeto = cotas.some((c) => c.maximo === null)
  const somaMaxima = cotas.reduce((s, c) => s + (c.maximo ?? 0), 0)
  // Vagas a preencher: turmas × professores por turma. Na dupla de Fotografia
  // que ela levou ao ChatGPT, 10 × 2 = 20 — e as cotas somavam 18 (§23.3).
  const alvo = noGrupo.size * porTurma

  /** O que a conta diz, em uma frase — ou nada, se estiver em pé. */
  const aviso = useMemo(() => {
    if (cotas.length === 0 || alvo === 0) return null
    if (porTurma > 1) {
      const conta = `${noGrupo.size} turma(s) × ${porTurma} professores = ${alvo} vagas`
      if (somaMinima > alvo) return `As cotas exigem ${somaMinima} vagas, e há ${conta}.`
      if (!semTeto && somaMaxima < alvo) {
        return `As cotas somam ${somaMaxima} vagas, e há ${conta} — faltam ${alvo - somaMaxima}.`
      }
      return null
    }
    if (somaMinima > alvo) {
      return `As cotas exigem ${turmas(somaMinima)} e só há ${turmas(alvo)} marcadas.`
    }
    if (!semTeto && somaMaxima < alvo) {
      return `As cotas comportam no máximo ${turmas(somaMaxima)}, e há ${turmas(alvo)} marcadas — ${
        alvo - somaMaxima
      } ficaria sem professor.`
    }
    return null
  }, [cotas.length, alvo, somaMinima, somaMaxima, semTeto, porTurma, noGrupo.size])

  /**
   * O que mostrar, e em que ordem.
   *
   * **A ordem é a do trabalho restante**, não a alfabética: primeiro as que
   * ainda não têm candidato, e dentro delas as de mais turmas, porque são as
   * que mais custam à mão. Medido na ESPM: 156 disciplinas rodam em mais de
   * uma turma, e a maior em 22 — uma lista alfabética faria a pessoa rolar
   * para descobrir onde está o trabalho.
   */
  const visiveis = useMemo(() => {
    const alvo = busca
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim()
      .toLowerCase()
    return disciplinas
      .filter(
        (d) =>
          !alvo ||
          d.nome
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .includes(alvo),
      )
      .sort(
        (a, b) =>
          Number(a.candidatos > 0) - Number(b.candidatos > 0) || b.turmas - a.turmas,
      )
      .slice(0, 60)
  }, [disciplinas, busca])

  const livres = (estado?.professores ?? []).filter(
    (p) => !cotas.some((c) => c.professor_id === p.id),
  )
  // Sobre a lista inteira, não só a dos livres: o homônimo que já está numa
  // cota continua existindo, e é ele que torna o outro ambíguo.
  const rotulos = rotulosDistintos(estado?.professores ?? [])

  async function gravar() {
    if (!escolhida || !estado) return
    setOcupado(true)
    setErro(null)
    try {
      await atribuicao.gravar(projeto, escolhida, {
        nome: estado.disciplina.nome,
        ofertas_ids: [...noGrupo],
        professores_por_oferta: porTurma,
        cotas: cotas.map((c) => ({
          professor_id: c.professor_id,
          minimo: c.minimo,
          maximo: c.maximo,
        })),
      })
      setSalvo(true)
      carregarLista()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível gravar')
    } finally {
      setOcupado(false)
    }
  }

  return (
    <>
      <header className="page-head">
        <h1>Atribuição de turmas</h1>
        <p className="page-head__note">
          Quem pode assumir cada disciplina, e quantas turmas. <strong>Quais</strong> turmas é o
          motor que escolhe, junto com o horário.
        </p>
      </header>

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      {disciplinas.length === 0 && !erro && (
        <div className="empty">
          <p className="empty__title">Nenhuma disciplina com mais de uma turma.</p>
          <p className="empty__body">
            Atribuir só faz sentido quando há escolha a fazer. Com uma turma por disciplina, quem
            dá a aula é uma linha da grade curricular.
          </p>
        </div>
      )}

      {disciplinas.length > 0 && (
        <section className="section">
          <div className="section__head">
            <h2>{disciplinas.length} disciplinas com mais de uma turma</h2>
            <input
              className="input"
              type="search"
              placeholder="filtrar pelo nome"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="filtrar disciplinas"
            />
          </div>

          <table className="comparar">
            <thead>
              <tr>
                <th scope="col">Disciplina</th>
                <th scope="col">Turmas</th>
                <th scope="col">Candidatos</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {visiveis.map((d) => (
                <tr key={d.id} className={escolhida === d.id ? 'ruled' : undefined}>
                  <td>{d.nome}</td>
                  <td>{d.turmas}</td>
                  <td>
                    {d.candidatos > 0 ? (
                      <span className="state state--ok">
                        <Icon name="ok" size={12} />
                        {d.candidatos}
                      </span>
                    ) : (
                      <span className="field__hint">a definir</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`btn ${escolhida === d.id ? 'btn--secondary' : 'btn--ghost'}`}
                      onClick={() => setEscolhida(escolhida === d.id ? null : d.id)}
                    >
                      {escolhida === d.id ? 'fechar' : 'abrir'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {visiveis.length === 0 && (
            <p className="field__hint">Nenhuma disciplina com “{busca}”.</p>
          )}
        </section>
      )}

      {estado && (
        <>
          <section className="section">
            <div className="section__head">
              <h2>Turmas de {estado.disciplina.nome}</h2>
              <span className="field__hint">{turmas(noGrupo.size)} no grupo</span>
            </div>

            <table className="comparar">
              <thead>
                <tr>
                  <th scope="col">No grupo</th>
                  <th scope="col">Turma</th>
                  <th scope="col">Série</th>
                  <th scope="col">Aulas/semana</th>
                  <th scope="col">Professor já definido</th>
                </tr>
              </thead>
              <tbody>
                {estado.ofertas.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`incluir ${o.turma_nome}`}
                        checked={noGrupo.has(o.id)}
                        onChange={(e) => {
                          const proximo = new Set(noGrupo)
                          if (e.target.checked) proximo.add(o.id)
                          else proximo.delete(o.id)
                          setNoGrupo(proximo)
                          setSalvo(false)
                        }}
                      />
                    </td>
                    <td>{o.turma_nome}</td>
                    <td>{o.serie}</td>
                    <td>{o.aulas_semanais}</td>
                    <td className="field__hint">
                      {o.professores_fixos.map((p) => p.nome).join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="section">
            <div className="section__head">
              <h2>Quem pode assumir</h2>
              <label className="row" data-tour="por-turma">
                <span className="field__label">Professores por turma</span>
                <select
                  className="select"
                  value={porTurma}
                  onChange={(e) => {
                    setPorTurma(Number(e.target.value))
                    setSalvo(false)
                  }}
                >
                  <option value={1}>um</option>
                  <option value={2}>dois — o sistema forma as duplas</option>
                  <option value={3}>três</option>
                </select>
              </label>
              <span className="field__hint">
                cotas somam {somaMinima}
                {semTeto ? '+' : somaMaxima !== somaMinima ? `–${somaMaxima}` : ''} de{' '}
                {turmas(alvo)}
              </span>
            </div>

            {aviso && (
              <p className="state state--pendencia" role="status">
                <Icon name="pendencia" size={14} />
                {aviso}
              </p>
            )}

            <table className="comparar">
              <thead>
                <tr>
                  <th scope="col">Professor</th>
                  <th scope="col">No mínimo</th>
                  <th scope="col">No máximo</th>
                  <th scope="col">Teto semanal</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {cotas.map((c, i) => {
                  const pessoa = estado.professores.find((p) => p.id === c.professor_id)
                  const trocar = (campo: 'minimo' | 'maximo', valor: number | null) => {
                    const proximo = [...cotas]
                    proximo[i] = { ...c, [campo]: valor }
                    setCotas(proximo)
                    setSalvo(false)
                  }
                  return (
                    <tr key={c.professor_id}>
                      <td>{pessoa?.nome ?? c.nome ?? c.professor_id}</td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          min={0}
                          value={c.minimo}
                          aria-label={`mínimo de ${pessoa?.nome ?? ''}`}
                          onChange={(e) => trocar('minimo', Number(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          min={0}
                          value={c.maximo ?? ''}
                          placeholder="sem teto"
                          aria-label={`máximo de ${pessoa?.nome ?? ''}`}
                          onChange={(e) =>
                            trocar('maximo', e.target.value === '' ? null : Number(e.target.value))
                          }
                        />
                      </td>
                      <td className="field__hint">
                        {pessoa?.max_aulas_semanais == null
                          ? '—'
                          : `${pessoa.max_aulas_semanais} aulas`}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => {
                            setCotas(cotas.filter((_, j) => j !== i))
                            setSalvo(false)
                          }}
                        >
                          tirar
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {livres.length > 0 && (
              <label className="field">
                <span className="field__hint">Acrescentar candidato</span>
                <select
                  className="select"
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return
                    setCotas([...cotas, { professor_id: e.target.value, minimo: 1, maximo: 1 }])
                    setSalvo(false)
                  }}
                >
                  <option value="">escolha…</option>
                  {livres.map((p) => (
                    <option key={p.id} value={p.id}>
                      {rotulos.get(p.id) ?? p.nome}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </section>

          <div className="solta__acoes no-print">
            <button
              type="button"
              className="btn btn--primary"
              disabled={ocupado}
              onClick={() => void gravar()}
            >
              {noGrupo.size === 0 ? 'Desfazer o grupo' : 'Gravar'}
            </button>
            {salvo && (
              <span className="state state--ok">
                <Icon name="ok" size={14} />
                Gravado. O motor decide as turmas ao gerar a grade.
              </span>
            )}
          </div>
        </>
      )}
    </>
  )
}
